/**
 * useOnboardingStepNavigation - Handles step-click routing in the onboarding checklist
 *
 * Each step navigates the user to the relevant feature:
 *   signed_up → auth tab (if not yet registered)
 *   spotted   → activates spot mode on the map
 *   favorited → opens search tab
 *   messaged  → opens contact tab
 *
 * @atomic - single responsibility: step-click navigation logic (<60 lines)
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '../services/toastService'
import { createLogger } from '../utils/logger'
import { trackEvent } from '../utils/analytics'

const logger = createLogger('OnboardingStepNav')

const AUTH_REQUIRED_STEPS = new Set(['spotted', 'favorited', 'messaged'])

/**
 * @param {{ progress, isAuthenticated, changeTab, activateSpotMode }} opts
 * @returns {(step: string) => void} handleStepClick
 */
export function useOnboardingStepNavigation({ progress, isAuthenticated, changeTab, activateSpotMode }) {
  const { t } = useTranslation()

  return useCallback((step) => {
    const isDone = !!progress[step]
    const isLocked = !isAuthenticated && AUTH_REQUIRED_STEPS.has(step)

    if (isDone) return

    trackEvent('Onboarding', 'step_clicked', step)

    if (isLocked) {
      logger.info('Locked step clicked, opening auth tab')
      changeTab('auth', 'expanded')
      toast.info(t('onboarding.welcome.progress.hints.signUpRequired'))
      return
    }

    switch (step) {
      case 'signed_up':
        if (!isAuthenticated) {
          changeTab('auth', 'expanded')
          toast.info(t('onboarding.welcome.progress.hints.signUpRequired'))
        }
        break

      case 'spotted':
        changeTab('search', 'minimized')
        toast.success(t('onboarding.welcome.progress.hints.spotMode'))
        setTimeout(() => activateSpotMode(), 300)
        break

      case 'favorited':
        changeTab('search', 'expanded')
        toast.info(t('onboarding.welcome.progress.hints.favorite'))
        break

      case 'messaged':
        changeTab('contact', 'expanded')
        toast.info(t('onboarding.welcome.progress.hints.message'))
        break

      default:
        logger.warn('Unknown onboarding step', { step })
    }
  }, [progress, isAuthenticated, changeTab, activateSpotMode, t])
}
